<template>
  <div class="tiptap-wrapper" :class="{ 'is-disabled': disabled }">
    <div v-if="editor" class="tiptap-toolbar">
      <div class="tiptap-toolbar-group">
        <button
          type="button"
          class="tiptap-btn"
          :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
          @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
          title="Heading 1"
        >H1</button>
        <button
          type="button"
          class="tiptap-btn"
          :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
          @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
          title="Heading 2"
        >H2</button>
        <button
          type="button"
          class="tiptap-btn"
          :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
          @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
          title="Heading 3"
        >H3</button>
      </div>
      <div class="tiptap-toolbar-separator"></div>
      <div class="tiptap-toolbar-group">
        <button
          type="button"
          class="tiptap-btn"
          :class="{ 'is-active': editor.isActive('bold') }"
          @click="editor.chain().focus().toggleBold().run()"
          title="Bold"
        ><strong>B</strong></button>
        <button
          type="button"
          class="tiptap-btn"
          :class="{ 'is-active': editor.isActive('italic') }"
          @click="editor.chain().focus().toggleItalic().run()"
          title="Italic"
        ><em>I</em></button>
        <button
          type="button"
          class="tiptap-btn"
          :class="{ 'is-active': editor.isActive('underline') }"
          @click="editor.chain().focus().toggleUnderline().run()"
          title="Underline"
        ><u>U</u></button>
        <button
          type="button"
          class="tiptap-btn"
          :class="{ 'is-active': editor.isActive('strike') }"
          @click="editor.chain().focus().toggleStrike().run()"
          title="Strikethrough"
        ><s>S</s></button>
      </div>
      <div class="tiptap-toolbar-separator"></div>
      <div class="tiptap-toolbar-group">
        <button
          type="button"
          class="tiptap-btn"
          :class="{ 'is-active': editor.isActive('bulletList') }"
          @click="editor.chain().focus().toggleBulletList().run()"
          title="Bullet list"
          aria-label="Bullet list"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" width="14" height="14">
            <line class="ql-stroke" x1="6" x2="15" y1="4" y2="4"></line>
            <line class="ql-stroke" x1="6" x2="15" y1="9" y2="9"></line>
            <line class="ql-stroke" x1="6" x2="15" y1="14" y2="14"></line>
            <line class="ql-stroke" x1="3" x2="3" y1="4" y2="4"></line>
            <line class="ql-stroke" x1="3" x2="3" y1="9" y2="9"></line>
            <line class="ql-stroke" x1="3" x2="3" y1="14" y2="14"></line>
          </svg>
        </button>
        <button
          type="button"
          class="tiptap-btn"
          :class="{ 'is-active': editor.isActive('orderedList') }"
          @click="editor.chain().focus().toggleOrderedList().run()"
          title="Ordered list"
          aria-label="Ordered list"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" width="14" height="14">
            <line class="ql-stroke" x1="7" x2="15" y1="4" y2="4"></line>
            <line class="ql-stroke" x1="7" x2="15" y1="9" y2="9"></line>
            <line class="ql-stroke" x1="7" x2="15" y1="14" y2="14"></line>
            <line class="ql-stroke" x1="2.5" x2="4.5" y1="5.5" y2="5.5"></line>
            <path class="ql-stroke" d="M3.5,6V3"></path>
            <path class="ql-stroke" d="M2.5,11h2a1,1,0,0,0,0-2H2.5a1,1,0,0,0,0,2Z"></path>
            <path class="ql-stroke" d="M4.5,14h-2l1.5-2H2.5"></path>
          </svg>
        </button>
        <button
          type="button"
          class="tiptap-btn"
          :class="{ 'is-active': editor.isActive('blockquote') }"
          @click="editor.chain().focus().toggleBlockquote().run()"
          title="Blockquote"
        >&ldquo;</button>
      </div>
      <div class="tiptap-toolbar-separator"></div>
      <div class="tiptap-toolbar-group">
        <button
          type="button"
          class="tiptap-btn"
          @click="editor.chain().focus().undo().run()"
          :disabled="!editor.can().chain().focus().undo().run()"
          title="Undo"
        >&#8617;</button>
        <button
          type="button"
          class="tiptap-btn"
          @click="editor.chain().focus().redo().run()"
          :disabled="!editor.can().chain().focus().redo().run()"
          title="Redo"
        >&#8618;</button>
      </div>
    </div>

    <editor-content :editor="editor" class="tiptap-content" />
  </div>
</template>

<script>
import { markRaw } from 'vue'
import { Editor } from '@tiptap/core'
import { EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'

export default {
  name: 'TiptapEditor',

  components: {
    EditorContent,
  },

  props: {
    modelValue: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue'],

  data() {
    return {
      editor: null,
    }
  },

  watch: {
    modelValue(value) {
      if (!this.editor) return
      if (this.editor.getHTML() === value) return
      this.editor.commands.setContent(value || '', false)
    },
    disabled(value) {
      if (!this.editor) return
      this.editor.setEditable(!value)
    },
  },

  mounted() {
    this.editor = markRaw(new Editor({
      content: this.modelValue || '',
      editable: !this.disabled,
      extensions: [
        StarterKit,
        Placeholder.configure({
          placeholder: this.placeholder,
        }),
      ],
      onUpdate: () => {
        this.$emit('update:modelValue', this.editor.getHTML())
      },
    }))
  },

  beforeUnmount() {
    if (this.editor) {
      this.editor.destroy()
    }
  },
}
</script>

<style scoped lang="scss">
.tiptap-wrapper {
  border-radius: 3px;
  background-color: #fff;
  border: 1px solid rgba(61, 188, 158, 0.35);

  .tiptap-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px;
    padding: 6px 8px;
    border-radius: 3px 3px 0 0;
    background-color: #fff;
    border-bottom: 1px solid #e0e0e0;

    .tiptap-toolbar-group {
      display: flex;
      align-items: center;
      gap: 1px;
    }

    .tiptap-toolbar-separator {
      width: 1px;
      height: 20px;
      background: #ccc;
      margin: 0 4px;
    }

    .tiptap-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 26px;
      height: 26px;
      padding: 0 4px;
      border: none;
      background: transparent;
      border-radius: 3px;
      cursor: pointer;
      font-size: 13px;
      color: #444;
      transition: background 0.15s;
      line-height: 1;

      svg {
        .ql-stroke {
          stroke: currentColor;
          stroke-width: 1.5;
          fill: none;
        }
        .ql-fill {
          fill: currentColor;
        }
      }

      &:hover {
        background: #e8e8e8;
        color: #111;
      }

      &.is-active {
        background: #3dbc9e20;
        color: #3dbc9e;
      }

      &:disabled {
        opacity: 0.35;
        cursor: default;
      }
    }
  }

  &.is-disabled {
    opacity: 0.7;
    pointer-events: none;
    background-color: #e9e9e9;
  }

  .tiptap-content {
    :deep(.tiptap) {
      outline: none;
      padding: 12px 15px;
      min-height: 150px;
      font-size: 13px;
      line-height: 1.6;
      color: #333;

      p.is-editor-empty:first-child::before {
        content: attr(data-placeholder);
        color: #aaa;
        pointer-events: none;
        float: left;
        height: 0;
      }

      p { margin: 0 0 6px; }
      h1, h2, h3 { margin: 8px 0 4px; }
      ul, ol { padding-left: 20px; margin: 0 0 6px; }
      blockquote {
        border-left: 3px solid #ccc;
        padding-left: 12px;
        color: #666;
        margin: 6px 0;
      }
    }
  }
}
</style>
